import React, { useEffect, useState, useCallback } from "react";
import {
    Box, Card, CardContent, Typography, CircularProgress,
    Chip, Skeleton, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tooltip as MuiTooltip,
} from "@mui/material";
import {
    ResponsiveContainer, AreaChart, Area,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend,
} from "recharts";
import { API_ENDPOINTS, getData } from "../../services/apiService.js";
import {
    CurrencyRupeeRounded as RupeeIcon,
    Inventory2Rounded as BookingIcon,
    TrendingUpRounded as TrendIcon,
    LocalShippingRounded as TruckIcon,
    AccountBalanceWalletRounded as PlatformIcon,
    LeaderboardRounded as LeaderIcon,
    HandymanRounded as HandlingIcon,
    PeopleAltRounded as AdoptionIcon,
} from "@mui/icons-material";

// ── Brand palette (mirrors dashboard) ────────────────────────────────────
const T = {
    teal:    "#007C84",
    tealDk:  "#016268",
    tealLt:  "#E0F4F5",
    orange:  "#FF7900",
    orangeLt:"#FFF0E0",
    indigo:  "#4F46E5",
    indigoLt:"#EEF2FF",
    emerald: "#059669",
    emeraldLt:"#D1FAE5",
    amber:   "#D97706",
    amberLt: "#FEF3C7",
    rose:    "#E11D48",
    roseLt:  "#FFE4E6",
    bg:      "#F7FAFC",
    surface: "#FFFFFF",
    border:  "#E2E8F0",
    text:    "#0F172A",
    muted:   "#64748B",
};

// ── Helpers ───────────────────────────────────────────────────────────────
const FILTER_MAP = { today: "today", week: "this_week", month: "this_month", year: "this_year" };

const DEMAND_STYLES = {
    VERY_HIGH: { bg: T.roseLt,    color: T.rose,    label: "Very High" },
    HIGH:      { bg: T.orangeLt,  color: T.orange,  label: "High"      },
    MEDIUM:    { bg: T.amberLt,   color: T.amber,   label: "Medium"    },
    LOW:       { bg: T.emeraldLt, color: T.emerald, label: "Low"       },
};

// trip_cost_v2 is the default engine — label it "Platform Pricing" so it's readable
const SOURCE_COLORS  = {
    platform_tier: T.teal, truck_owner: T.orange, trip_override: T.indigo,
    trip_cost_v2: T.teal, trip_cost_v1: "#94A3B8", unknown: "#94A3B8",
};
const SOURCE_LABELS  = {
    platform_tier: "Platform Tier", truck_owner: "Owner Price", trip_override: "Override",
    trip_cost_v2: "Platform Pricing", trip_cost_v1: "Legacy Pricing", unknown: "Unknown",
};

const fmt  = (n, prefix = "") => n == null ? "--" : `${prefix}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtK = n => n == null ? "--" : n >= 100000 ? `₹${(n/100000).toFixed(1)}L` : n >= 1000 ? `₹${(n/1000).toFixed(1)}k` : `₹${Number(n).toFixed(0)}`;
const fmtDate = d => new Date(d).toLocaleDateString("en-GB", { month: "short", day: "numeric" });

// ── Range toggle (same as dashboard) ─────────────────────────────────────
const RANGES = [
    { label: "Today", value: "today" },
    { label: "Week",  value: "week"  },
    { label: "Month", value: "month" },
    { label: "Year",  value: "year"  },
];
const RangeToggle = ({ value, onChange }) => (
    <Box sx={{ display: "flex", gap: 0.5, bgcolor: T.bg, borderRadius: "10px", p: 0.5, border: `1px solid ${T.border}` }}>
        {RANGES.map(r => (
            <Box key={r.value} onClick={() => onChange(r.value)} sx={{
                px: 1.5, py: 0.5, borderRadius: "8px", fontSize: 12, fontWeight: 600,
                cursor: "pointer", transition: "all 0.15s",
                bgcolor: value === r.value ? T.teal : "transparent",
                color:   value === r.value ? "#fff"  : T.muted,
                "&:hover": { bgcolor: value === r.value ? T.teal : T.tealLt, color: value === r.value ? "#fff" : T.teal },
                fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}>{r.label}</Box>
        ))}
    </Box>
);

// ── KPI tile ──────────────────────────────────────────────────────────────
const KpiTile = ({ title, value, sub, icon, iconBg, iconColor, accent, loading }) => (
    <Card sx={{
        flex: "1 1 0", minWidth: 150,
        borderRadius: "16px", border: `1px solid ${T.border}`,
        boxShadow: "0 1px 4px rgba(15,23,42,0.06)", overflow: "hidden",
    }}>
        <Box sx={{ height: 3, background: `linear-gradient(90deg, ${accent}, ${accent}66)` }} />
        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.4px", mb: 0.8, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        {title}
                    </Typography>
                    {loading
                        ? <Skeleton width={80} height={32} />
                        : <Typography sx={{ fontSize: 24, fontWeight: 800, color: T.text, lineHeight: 1.1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            {value}
                          </Typography>
                    }
                    {sub && !loading && (
                        <Typography sx={{ fontSize: 11, color: T.muted, mt: 0.5 }}>{sub}</Typography>
                    )}
                </Box>
                <Box sx={{ width: 42, height: 42, borderRadius: "12px", bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {React.cloneElement(icon, { sx: { fontSize: 20, color: iconColor } })}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ── Chart card wrapper ────────────────────────────────────────────────────
const ChartCard = ({ title, subtitle, loading, empty, children, height = 280 }) => (
    <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)", height: "100%" }}>
        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
            <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</Typography>
                {subtitle && <Typography sx={{ fontSize: 12, color: T.muted, mt: 0.3 }}>{subtitle}</Typography>}
            </Box>
            <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {loading   ? <CircularProgress size={28} sx={{ color: T.teal }} />
                : empty    ? <Typography fontSize={13} color="text.secondary">No data for this period</Typography>
                : children}
            </Box>
        </CardContent>
    </Card>
);

// ── Custom tooltip ────────────────────────────────────────────────────────
const ChartTip = ({ active, payload, label, rupee }) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{ bgcolor: "#fff", border: `1px solid ${T.border}`, borderRadius: "10px", p: 1.5, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", minWidth: 140 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.muted, mb: 0.5 }}>{label}</Typography>
            {payload.map(p => (
                <Box key={p.dataKey} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.text }}>
                        {p.name}: {rupee ? fmtK(p.value) : p.value}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

// ── Fill-rate mini bar ────────────────────────────────────────────────────
const FillBar = ({ rate }) => {
    const pct   = Math.round((rate || 0) * 100);
    const color = pct > 80 ? T.rose : pct > 60 ? T.orange : T.emerald;
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 52, height: 6, borderRadius: 3, bgcolor: T.border, overflow: "hidden" }}>
                <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: color, borderRadius: 3 }} />
            </Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color }}>{pct}%</Typography>
        </Box>
    );
};

// ── Main component ────────────────────────────────────────────────────────
export default function PricingAnalytics() {
    const [range,          setRange]          = useState("month");
    const [kpis,           setKpis]           = useState(null);
    const [kpisLoading,    setKpisLoading]    = useState(false);
    const [sourceChart,    setSourceChart]    = useState([]);
    const [sourceLoading,  setSourceLoading]  = useState(false);
    // distrib/distribLoading removed — demand multiplier endpoint no longer used
    const [leaderboard,    setLeaderboard]    = useState([]);
    const [lbLoading,      setLbLoading]      = useState(false);

    const apiFilter = FILTER_MAP[range];

    const fetchKpis = useCallback(async (f) => {
        setKpisLoading(true);
        try { const r = await getData(API_ENDPOINTS.pricingKpis, { filter: f }); setKpis(r.result); }
        catch (_) { setKpis(null); } finally { setKpisLoading(false); }
    }, []);

    const fetchSourceChart = useCallback(async (f) => {
        setSourceLoading(true);
        try { const r = await getData(API_ENDPOINTS.pricingSourceChart, { filter: f }); setSourceChart(r.result?.chartData || []); }
        catch (_) { setSourceChart([]); } finally { setSourceLoading(false); }
    }, []);

    // fetchDistrib removed — demand multiplier endpoint no longer needed

    const fetchLeaderboard = useCallback(async () => {
        setLbLoading(true);
        try { const r = await getData(API_ENDPOINTS.routeDemandLeaderboard); setLeaderboard(r.result?.leaderboard || []); }
        catch (_) { setLeaderboard([]); } finally { setLbLoading(false); }
    }, []);

    useEffect(() => {
        fetchKpis(apiFilter);
        fetchSourceChart(apiFilter);
    }, [apiFilter, fetchKpis, fetchSourceChart]);

    useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

    // ── Derived ───────────────────────────────────────────────────────────
    // Include ALL sources in totals — rename trip_cost_v2 label only
    const srcBreakdown  = kpis?.pricingSourceBreakdown ?? [];
    const totalRevenue  = srcBreakdown.reduce((s, x) => s + (Number(x.revenue) || 0), 0);
    const totalBookings = srcBreakdown.reduce((s, x) => s + (x.bookingCount || 0), 0);
    const avgBookingVal = totalBookings > 0 ? totalRevenue / totalBookings : null;

    // Cargo surcharge collected (actual fee on top of trip cost)
    const cargoSurcharge = kpis?.totalCargoSurcharge ?? null;

    // Owner pricing adoption (structural — how many trucks have custom pricing configured)
    const adoption = kpis?.ownerPricingAdoption ?? null;
    const adoptionPct = adoption?.totalTrucks > 0
        ? Math.round((adoption.ownerRateSet / adoption.totalTrucks) * 100) : null;

    // Source chart — total line + per-source lines that match actual active sources
    const knownSources  = ["platform_tier", "truck_owner", "trip_override", "trip_cost_v2", "trip_cost_v1"];
    const activeSources = srcBreakdown.map(s => s.source);
    const chartData = sourceChart.map(r => {
        const row = { date: fmtDate(r.date), Total: r.total ?? 0 };
        knownSources.filter(s => activeSources.includes(s)).forEach(s => {
            row[SOURCE_LABELS[s] ?? s] = r[s] ?? 0;
        });
        return row;
    });


    const rangeLabelMap = { today: "Today", week: "This Week", month: "This Month", year: "This Year" };

    return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>

            {/* ── Hero header ── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${T.teal} 0%, ${T.tealDk} 100%)`,
                borderRadius: "20px", p: 3, mb: 3,
                display: "flex", justifyContent: "space-between", alignItems: "center",
                boxShadow: "0 4px 20px rgba(0,124,132,0.2)",
            }}>
                <Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        Pricing Analytics
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
                        Demand multipliers, revenue by source, and route demand leaderboard
                    </Typography>
                </Box>
                <RangeToggle value={range} onChange={setRange} />
            </Box>

            {/* ── KPI tiles ── */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                <KpiTile title="Total Revenue"      value={totalRevenue > 0 ? fmtK(totalRevenue) : "--"}     sub={totalBookings > 0 ? `${totalBookings} bookings` : "No paid bookings"} icon={<RupeeIcon />}    iconBg={T.tealLt}    iconColor={T.teal}   accent={T.teal}   loading={kpisLoading} />
                <KpiTile title="Total Bookings"     value={totalBookings > 0 ? totalBookings : "--"}          sub={rangeLabelMap[range]}                                                  icon={<BookingIcon />}  iconBg={T.emeraldLt} iconColor={T.emerald}accent={T.emerald} loading={kpisLoading} />
                <KpiTile title="Avg Booking Value"  value={avgBookingVal != null ? fmtK(avgBookingVal) : "--"} sub="per booking"                                                         icon={<TrendIcon />}    iconBg={T.orangeLt}  iconColor={T.orange} accent={T.orange} loading={kpisLoading} />
                <KpiTile title="Cargo Surcharge"    value={cargoSurcharge != null && cargoSurcharge > 0 ? fmtK(cargoSurcharge) : (kpisLoading ? "--" : "₹0")} sub="collected this period" icon={<HandlingIcon />} iconBg={T.indigoLt}  iconColor={T.indigo} accent={T.indigo} loading={kpisLoading} />
                <KpiTile title="Owner Pricing Adoption" value={adoptionPct != null ? `${adoptionPct}%` : "--"} sub={adoption ? `${adoption.ownerRateSet} of ${adoption.totalTrucks} trucks · all-time` : "fleet-wide snapshot"} icon={<AdoptionIcon />} iconBg={T.amberLt} iconColor={T.amber} accent={T.amber} loading={kpisLoading} />
            </Box>

            {/* ── Pricing source breakdown pills ── */}
            {!kpisLoading && srcBreakdown.length > 0 && (
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
                    {srcBreakdown.map(s => {
                        const color = SOURCE_COLORS[s.source] ?? "#94A3B8";
                        const label = SOURCE_LABELS[s.source] ?? s.source;
                        const pct   = totalRevenue > 0 ? Math.round((s.revenue / totalRevenue) * 100) : 0;
                        return (
                            <Card key={s.source} sx={{
                                flex: "1 1 0", minWidth: 160, borderRadius: "14px",
                                border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.05)",
                                borderLeft: `4px solid ${color}`, overflow: "visible",
                            }}>
                                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <Box>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.4px", mb: 0.5 }}>
                                                {label}
                                            </Typography>
                                            <Typography sx={{ fontSize: 20, fontWeight: 800, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                                {fmtK(s.revenue)}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: T.muted, mt: 0.3 }}>
                                                {s.bookingCount} bookings
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={`${pct}%`}
                                            size="small"
                                            sx={{ bgcolor: `${color}18`, color, fontWeight: 700, fontSize: 11, border: `1px solid ${color}33`, borderRadius: "8px" }}
                                        />
                                    </Box>
                                    {/* mini progress bar */}
                                    <Box sx={{ mt: 1.5, height: 4, borderRadius: 2, bgcolor: T.border, overflow: "hidden" }}>
                                        <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: color, borderRadius: 2, transition: "width 0.4s" }} />
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>
            )}

            {/* ── Charts row ── */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                {/* Revenue by source — stacked area */}
                <Box sx={{ flex: "1 1 55%", minWidth: 320 }}>
                    <ChartCard title="Revenue by Pricing Source" subtitle="Stacked area — contribution of each pricing model over time" loading={sourceLoading} empty={chartData.length === 0}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                                <defs>
                                    <linearGradient id="gTotal" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%"  stopColor={T.teal}  stopOpacity={0.3} />
                                        <stop offset="100%" stopColor={T.teal} stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="4 4" stroke={T.border} vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} />
                                <YAxis tickFormatter={v => v >= 1000 ? `₹${(v/1000).toFixed(0)}k` : `₹${v}`} tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} width={48} />
                                <Tooltip content={<ChartTip rupee />} />
                                <Legend wrapperStyle={{ fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", paddingTop: 8 }} />
                                {/* Total always shown */}
                                <Area type="monotone" dataKey="Total" name="Total" stroke={T.teal} strokeWidth={2.5} fill="url(#gTotal)" dot={false} activeDot={{ r: 4 }} />
                                {/* Per-source lines (dynamic — only rendered if the source exists in this period) */}
                                {Object.keys(chartData[0] ?? {}).filter(k => k !== "date" && k !== "Total").map((key, i) => {
                                    const colors = [T.orange, T.indigo, T.emerald, T.rose];
                                    return <Area key={key} type="monotone" dataKey={key} name={key} stackId="src" stroke={colors[i % colors.length]} fill={colors[i % colors.length]} fillOpacity={0.12} strokeWidth={1.5} dot={false} activeDot={{ r: 3 }} />;
                                })}
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Box>

                {/* Owner Pricing Adoption */}
                <Box sx={{ flex: "1 1 40%", minWidth: 260 }}>
                    <ChartCard title="Pricing Source Adoption" subtitle="Fleet-wide configuration snapshot · not date-filtered" loading={kpisLoading} empty={!adoption}>
                        <Box sx={{ width: "100%", px: 1 }}>
                            {/* Platform Tier vs Owner Rate breakdown */}
                            {adoption && (() => {
                                const total = adoption.totalTrucks || 1;
                                const ownerPct = Math.round((adoption.ownerRateSet / total) * 100);
                                const platformPct = 100 - ownerPct;
                                const rows = [
                                    { label: "Platform Tier",   value: total - (adoption.ownerRateSet || 0), pct: platformPct, color: T.teal   },
                                    { label: "Owner-Set Rate",  value: adoption.ownerRateSet || 0,           pct: ownerPct,     color: T.orange },
                                ];
                                return (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, width: "100%" }}>
                                        {/* Stacked summary bar */}
                                        <Box>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.muted }}>
                                                    Fleet breakdown ({total} trucks total)
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", height: 10, borderRadius: "6px", overflow: "hidden", bgcolor: T.border }}>
                                                <Box sx={{ width: `${platformPct}%`, bgcolor: T.teal,   transition: "width 0.5s" }} />
                                                <Box sx={{ width: `${ownerPct}%`,   bgcolor: T.orange, transition: "width 0.5s" }} />
                                            </Box>
                                            <Box sx={{ display: "flex", gap: 2, mt: 0.8 }}>
                                                {rows.map(r => (
                                                    <Box key={r.label} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: r.color }} />
                                                        <Typography sx={{ fontSize: 11, color: T.muted }}>{r.label}: {r.pct}%</Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>

                                        {/* Per-row detail */}
                                        {rows.map(r => (
                                            <Box key={r.label}>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: r.color }} />
                                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text }}>{r.label}</Typography>
                                                    </Box>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: r.color }}>{r.value}</Typography>
                                                        <Typography sx={{ fontSize: 11, color: T.muted }}>trucks</Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ height: 6, borderRadius: "4px", bgcolor: T.bg, overflow: "hidden" }}>
                                                    <Box sx={{ width: `${r.pct}%`, height: "100%", bgcolor: r.color, borderRadius: "4px", opacity: 0.7, transition: "width 0.5s" }} />
                                                </Box>
                                            </Box>
                                        ))}

                                        {/* Top route chip */}
                                        {kpis?.topRoute && (
                                            <Box sx={{ mt: 1, p: 1.5, bgcolor: T.tealLt, borderRadius: "10px", border: `1px solid ${T.teal}22` }}>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.teal, textTransform: "uppercase", letterSpacing: "0.4px", mb: 0.3 }}>
                                                    Top Searched Route (7d)
                                                </Typography>
                                                <Typography sx={{ fontSize: 14, fontWeight: 800, color: T.text }}>
                                                    {kpis.topRoute.startCity}
                                                    <Box component="span" sx={{ mx: 1, color: T.muted, fontWeight: 400 }}>→</Box>
                                                    {kpis.topRoute.endCity}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: T.muted, mt: 0.3 }}>
                                                    {kpis.topRoute.searches7d} searches
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })()}
                        </Box>
                    </ChartCard>
                </Box>
            </Box>

            {/* ── Route demand leaderboard ── */}
            <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}>
                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <LeaderIcon sx={{ fontSize: 20, color: T.teal }} />
                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                    Route Demand Leaderboard
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: 12, color: T.muted, mt: 0.3 }}>
                                Top 10 routes by search volume (last 7 days) · fill rate from active trips
                            </Typography>
                        </Box>
                        {lbLoading && <CircularProgress size={20} sx={{ color: T.teal }} />}
                    </Box>

                    {leaderboard.length === 0 && !lbLoading ? (
                        <Typography sx={{ color: T.muted, textAlign: "center", py: 4, fontSize: 13 }}>
                            No route search data available
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.4px", bgcolor: T.bg, borderBottom: `2px solid ${T.border}` } }}>
                                        <TableCell sx={{ width: 36 }}>#</TableCell>
                                        <TableCell>Route</TableCell>
                                        <TableCell align="right">Searches 7d</TableCell>
                                        <TableCell align="right">Active Trips</TableCell>
                                        <TableCell align="right">Max Fill</TableCell>
                                        <TableCell align="right">Avg Fill</TableCell>
                                        <TableCell align="center">Demand</TableCell>
                                        <TableCell align="right">Suggested Uplift</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {leaderboard.map((row) => {
                                        const lvl = DEMAND_STYLES[row.demandLevel] ?? DEMAND_STYLES.LOW;
                                        return (
                                            <TableRow key={row.rank} hover sx={{ "&:last-child td": { border: 0 } }}>
                                                <TableCell>
                                                    <Box sx={{
                                                        width: 24, height: 24, borderRadius: "6px",
                                                        bgcolor: row.rank <= 3 ? T.tealLt : T.bg,
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                    }}>
                                                        <Typography sx={{ fontSize: 11, fontWeight: 800, color: row.rank <= 3 ? T.teal : T.muted }}>
                                                            {row.rank}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                                                        {row.startCity}
                                                        <Box component="span" sx={{ mx: 0.8, color: T.muted, fontWeight: 400 }}>→</Box>
                                                        {row.endCity}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text }}>{row.searches7d}</Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography sx={{ fontSize: 13, color: T.text }}>{row.activeTrips}</Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <MuiTooltip title={`${(row.maxFillRate * 100).toFixed(0)}% capacity`}>
                                                        <span><FillBar rate={row.maxFillRate} /></span>
                                                    </MuiTooltip>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography sx={{ fontSize: 12, color: T.muted }}>
                                                        {(row.avgFillRate * 100).toFixed(0)}%
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip label={lvl.label} size="small" sx={{
                                                        bgcolor: lvl.bg, color: lvl.color,
                                                        fontWeight: 700, fontSize: 11, borderRadius: "7px", height: 22,
                                                        border: `1px solid ${lvl.color}33`,
                                                    }} />
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row.suggestedUpliftPct > 0 ? (
                                                        <Chip
                                                            label={`+${row.suggestedUpliftPct}%`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: row.suggestedUpliftPct >= 30 ? T.roseLt : T.orangeLt,
                                                                color:   row.suggestedUpliftPct >= 30 ? T.rose   : T.orange,
                                                                fontWeight: 700, fontSize: 11, borderRadius: "7px", height: 22,
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography sx={{ fontSize: 12, color: T.muted }}>—</Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
